<template>
  <SoWidget no-padding no-border plain>
    <div class="d-flex align-center mb-2">
      <h4 class="text-h4">Timeline</h4>
      <v-spacer />
      <SoButton
        title="Add Note"
        class="pa-1"
        prepend-icon="plus"
        data-testid="add-note-button"
        @click="addNoteDialogOpen = true"
      />
    </div>
    <SoSelect
      v-model="selectedFilters"
      :items="filterOptions"
      type="multiple"
      class="mb-4 timeline-filter"
      placeholder="Filter"
      data-testid="timeline-filter"
    />
    <SoAlert
      v-if="juniferComms.isFetching && juniferComms.isPlaceholderData"
      type="info"
      class="mb-4"
      data-testid="junifer-comms-loading"
    >
      Loading Junifer communications…
    </SoAlert>
    <SoAlert
      v-else-if="isJuniferAccountMissing"
      type="info"
      class="mb-4"
      data-testid="junifer-comms-no-account"
    >
      No Junifer account found, so there are no Junifer communications to show
    </SoAlert>
    <SoAlert
      v-else-if="juniferComms.isError"
      type="error"
      class="mb-4"
      data-testid="junifer-comms-error"
    >
      <div class="d-flex align-center justify-space-between ga-4">
        Couldn't load Junifer communications, so the timeline may be missing
        letters and emails.
        <SoButton
          title="Retry"
          data-test-id="junifer-comms-retry"
          @click="juniferComms.refetch()"
        />
      </div>
    </SoAlert>
    <MeterAppointmentSlot
      ref="feed"
      :feed-item-list="filteredFeedItems"
      :is-loading="isLoading"
      :pagination-reset-key="selectedFilters.join(',')"
      height="calc(100vh - 204px)"
    />
  </SoWidget>
  <AddNoteModal
    v-model="addNoteDialogOpen"
    :account-number="account.data.number"
  />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoWidget from "@/components/SoWidget.vue"
  import MeterAppointmentSlot from "../activityFeed/MeterAppointmentSlot.vue"
  import AddNoteModal from "../activityFeed/addNoteModal/AddNoteModal.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { IItemsByDate } from "@/models/activityFeed/ActivityFeedItem"
  import { mapState } from "pinia"
  import { Utility } from "@/types/Utility"
  import { DcaConfigurationService } from "@/services/DcaConfigurationService"
  import { sortDayEvents } from "@/services-handlers/getActivityFeedEvents"
  import { isBackofficeApiError } from "@/api/backofficeRest/backofficeApiError"

  interface IFilterOptions {
    title: string
    value: Utility
  }

  export default defineComponent({
    components: {
      SoWidget,
      MeterAppointmentSlot,
      SoSelect,
      SoButton,
      SoAlert,
      AddNoteModal,
    },
    data() {
      return {
        addNoteDialogOpen: false,
        pageItems: [],
        selectedFilters: [
          Utility.Case,
          Utility.Note,
          Utility.Junifer,
          Utility.Freshdesk,
          Utility.Complaint,
          Utility.Email,
          Utility.Communication,
          Utility.Bill,
          Utility.Payment,
          Utility.Other,
          Utility.Debt,
          Utility.Appointment,
        ] as Utility[],
        filterOptions: [
          { title: "Bill", value: Utility.Bill },
          { title: "Communication", value: Utility.Communication },
          { title: "Complaint", value: Utility.Complaint },
          { title: "Email", value: Utility.Email },
          { title: "Freshdesk", value: Utility.Freshdesk },
          { title: "Junifer Ticket", value: Utility.Junifer },
          { title: "Cases", value: Utility.Case },
          { title: "Payments", value: Utility.Payment },
          { title: "Note", value: Utility.Note },
          { title: "Other", value: Utility.Other },
          { title: "Debt", value: Utility.Debt },
          { title: "Site Visit", value: Utility.Appointment },
        ] as IFilterOptions[],
        dcaConfigurationService: DcaConfigurationService,
      }
    },
    computed: {
      ...mapState(useCurrentAccountStore, [
        "account",
        "activityFeedEvents",
        "juniferComms",
      ]),
      isJuniferAccountMissing(): boolean {
        const error = this.juniferComms.error
        return (
          isBackofficeApiError(error) && error.errorCode === "account_not_found"
        )
      },
      filteredFeedItems(): IItemsByDate[] {
        const filtered: IItemsByDate[] = []
        const debtFilterName = Utility.Debt

        for (const event of this.activityFeedEvents.data) {
          let items = event.items.filter((item) =>
            this.selectedFilters.includes(item.type),
          )

          const includedItems = new Set(items)

          if (this.selectedFilters.includes(debtFilterName)) {
            const debtItems = event.items.filter(
              (item) => item.props.chip === "DCA" && !includedItems.has(item),
            )
            items = sortDayEvents(items.concat(debtItems))
          }

          if (items.length) {
            filtered.push({
              date: event.date,
              items,
            })
          }
        }
        return filtered
      },
      isLoading() {
        return this.activityFeedEvents.isFetching
      },
    },
  })
</script>

<style scoped>
  .timeline-filter {
    width: fit-content;
  }
</style>
